import {Checkbox, Group, NumberInput, Select, Stack, Text, TextInput, Tooltip} from '@mantine/core';
import React from 'react';

import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import {ObjectType} from 'app-core/framework/CoreObjectConstants';
import {getModelTypeConstantForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import CollapsibleCard from 'ui-library/CollapsibleCard';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import {immutableRemoveAt} from 'util/ImmutableListUtil';
import {capitalize, typeToName} from 'util/NameUtil';
import {JSONValue, Override} from 'util/Types';

import {setValueInConfig} from '../../step-config-settings/StepConfigSettingsUtils';
import {
  ConfigShapePath,
  CustomComponentEditorProps,
  FIELD_ORDER_UI_SCHEMA_KEY,
} from '../../StepConfigEditorTypes';
import StepConfigEditorRouter from '../StepConfigEditorRouter';
import DomainTypeSchemaSelector, {isDomainDefinition} from './DomainTypeSchemaSelector';

const VARIABLE_DEFINITION_CONFIG_SHAPE = PIPELINE_STEP_SHAPES[PipelineStepType.DEFINE_TEMPLATE_VARIABLES][0]
  .config.variables.elementSpec.fields.definition as GetPipelineConfigSpecType<'taggedUnion'>;

// The first choice (index 0) is 'array', which wraps the actual non-array choices.
const ARRAY_CHOICE = VARIABLE_DEFINITION_CONFIG_SHAPE.choices[0];
const ELEMENT_SPEC_CONFIG_SHAPE = ARRAY_CHOICE.fields.elementSpec as GetPipelineConfigSpecType<'taggedUnion'>;

const DOMAIN_CHOICE_SHAPE = ELEMENT_SPEC_CONFIG_SHAPE.choices.find((choice) => {
  const kindField = choice.fields.kind as GetPipelineConfigSpecType<'string'>;
  return kindField.value === 'domain';
}) as GetPipelineConfigSpecType<'shape'>;

const DOMAIN_TYPES_ARRAY_SPEC = DOMAIN_CHOICE_SHAPE.fields.types as GetPipelineConfigSpecType<'array'>;
const DOMAIN_TYPES_ENUM_SPEC = DOMAIN_TYPES_ARRAY_SPEC.elementSpec as GetPipelineConfigSpecType<'enum'>;
const DOMAIN_TYPE_CHOICES = DOMAIN_TYPES_ENUM_SPEC.choices as ReadonlyArray<string>;

type PrimitiveKind = 'boolean' | 'integer' | 'number' | 'string';

const PRIMITIVE_KINDS: ReadonlySet<PrimitiveKind> = new Set(['boolean', 'integer', 'number', 'string']);

const PRIMITIVE_DISPLAY_NAMES: Record<PrimitiveKind, string> = {
  boolean: 'Boolean',
  integer: 'Integer',
  number: 'Decimal',
  string: 'Text',
};

// All non-domain primitive kinds available in the variable definition spec.
const PRIMITIVE_TYPE_CHOICES: ReadonlyArray<PrimitiveKind> = ELEMENT_SPEC_CONFIG_SHAPE.choices
  .map((choice) => (choice.fields.kind as GetPipelineConfigSpecType<'string'>).value as string)
  .filter((kind): kind is PrimitiveKind => PRIMITIVE_KINDS.has(kind as PrimitiveKind));

function getDomainTypeDisplayName(domainType: string): string {
  return capitalize(typeToName(getModelTypeConstantForObjectType(domainType as ObjectType)));
}

const TYPE_GROUP_BASIC = 'Basic';
const TYPE_GROUP_BENCHLING = 'Benchling';

/**
 * Combined "Type" dropdown values. A single value identifies either a primitive kind (`boolean`,
 * `integer`, `number`, `string`) or a Benchling domain object type (`PLATE`, `CONTAINER`, etc.).
 *
 * Domain types map to a `{kind: 'domain', types: [<value>]}` definition; primitives map directly to
 * `{kind: <value>}`. Both groups are labeled because Mantine's `Select` renders grouped items
 * before ungrouped items, so the only way to put the basic types first is to group them too.
 */
const COMBINED_TYPE_OPTIONS: Array<{value: string; label: string; group: string}> = [
  ...PRIMITIVE_TYPE_CHOICES.map((kind) => ({
    value: kind,
    label: PRIMITIVE_DISPLAY_NAMES[kind] ?? kind,
    group: TYPE_GROUP_BASIC,
  })),
  ...DOMAIN_TYPE_CHOICES.map((domainType) => ({
    value: domainType,
    label: getDomainTypeDisplayName(domainType),
    group: TYPE_GROUP_BENCHLING,
  })),
];

export type VariableConfig = PipelineStepConfig<
  PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  0,
  'draft'
>['variables'][number];

type VariableDefinition = VariableConfig['definition'];

export type NonArrayDefinition = Exclude<VariableDefinition, {kind: 'array'}>;

type ArrayDefinition = Extract<VariableDefinition, {kind: 'array'}>;

type DomainKindUnion = NonArrayDefinition extends infer T
  ? T extends {kind: 'domain'; types: ReadonlyArray<infer U>}
    ? U
    : never
  : never;

const DEFAULT_VARIABLE: VariableConfig = {
  name: '',
  definition: {kind: 'string', required: true},
  value: null,
};

// `kind` is one of the primitive kinds ('boolean'/'integer'/'number'/'string') or a Benchling domain
// object type (e.g. 'PLATE', 'CONTAINER', ...) — the combined Type dropdown emits both. Domain types
// fall through to the default case, which constructs `{kind: 'domain', types: [<domain type>], ...}`.
// minValue/maxValue only exist on 'integer', not 'number', per the YAML schema.
function getDefaultConfigForType(kind: string): NonArrayDefinition {
  switch (kind) {
    case 'boolean':
      return {kind: 'boolean', required: true};
    case 'integer':
      return {kind: 'integer', required: true, minValue: null, maxValue: null};
    case 'number':
      return {kind: 'number', required: true};
    case 'string':
      return {kind: 'string', required: true};
    default:
      return {
        kind: 'domain',
        required: true,
        types: [kind as DomainKindUnion],
        schemaIds: null,
        schemaInterfaceIds: null,
      };
  }
}

function getCombinedTypeForDefinition(definition: NonArrayDefinition | null | undefined): string | null {
  if (definition == null) {
    return null;
  }
  if (definition.kind === 'domain') {
    return definition.types[0] ?? null;
  }
  return definition.kind;
}

function convertDefinitionToArray(definition: VariableDefinition): ArrayDefinition {
  if (definition?.kind === 'array') {
    return definition;
  }
  return {kind: 'array', required: true, elementSpec: definition ?? {kind: 'string', required: true}};
}

function convertDefinitionFromArray(definition: VariableDefinition): NonArrayDefinition {
  return definition?.kind === 'array' ? definition.elementSpec : definition;
}

type DefineTemplateVariablesEditorProps = Override<
  CustomComponentEditorProps,
  {config: PipelineStepConfig<PipelineStepType.DEFINE_TEMPLATE_VARIABLES, 0, 'draft'>}
>;

interface VariableValueEditorProps {
  definition: VariableDefinition;
  value: JSONValue;
  isMultiSelect: boolean;
  onChange: (value: JSONValue) => void;
  disabled?: boolean;
}

/**
 * Editor for a variable's reference value, delegated to `StepConfigEditorRouter` so each kind
 * (primitive, domain, array) reuses the standard per-kind input. The value is wrapped under a
 * `value` field so the router's `ShapeEditor` renders the field label and dispatches to the right
 * `FieldEditor`. See `AnalysisTemplateVariablesForm` for the same pattern applied to template
 * variable bindings.
 */
function VariableValueEditor({
  definition,
  value,
  isMultiSelect,
  onChange,
  disabled,
}: VariableValueEditorProps): JSX.Element {
  if (definition == null) {
    return <></>;
  }
  return (
    <StepConfigEditorRouter
      config={{value}}
      configShape={{
        kind: 'shape',
        fields: {value: definition as PipelineConfigJsonSpec},
        required: true,
      }}
      uiSchema={{
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['value'],
        value: {
          displayName: isMultiSelect ? 'Reference values' : 'Reference value',
          infoText:
            'Used to test the analysis as you design the template. When the template is applied, users will provide a different value.',
          configShapePath: ['value'],
        },
      }}
      onChangeConfig={(oldConfig: Record<string, JSONValue>, path: ConfigShapePath, newValue: JSONValue) => {
        const newConfig = setValueInConfig(oldConfig, path, newValue);
        onChange(newConfig.value);
      }}
      disabled={disabled}
    />
  );
}

interface VariableEditorProps {
  index: number;
  config: VariableConfig;
  onChangeConfig: (config: VariableConfig) => void;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

function VariableEditorCard({
  index,
  config,
  onChangeConfig,
  onRemove,
  canRemove,
  disabled,
}: VariableEditorProps): JSX.Element {
  const isMultiSelect = config.definition?.kind === 'array';
  const scalarDefinition = convertDefinitionFromArray(config.definition);
  const combinedType = getCombinedTypeForDefinition(scalarDefinition);
  const isDomain = scalarDefinition?.kind === 'domain';

  const cardTitle = config.name && config.name.length > 0 ? config.name : `Variable ${index + 1}`;

  return (
    <CollapsibleCard
      leftHeader={<Text weight={600}>{cardTitle}</Text>}
      rightHeader={
        <Tooltip label={canRemove ? 'Remove variable' : 'Cannot remove the last variable'}>
          <span // Stop event propagation, since the header is also clickable
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DeleteIcon
              icon="trash-can"
              altText="Remove variable"
              disabled={!canRemove || disabled}
              onClick={onRemove}
            />
          </span>
        </Tooltip>
      }
      aria-label={cardTitle}
      initiallyExpanded={true}
    >
      <Stack spacing="sm" p="xs">
        <TextInput
          label="Name"
          required={true}
          value={config.name ?? ''}
          placeholder="Variable name"
          onChange={(e) => {
            onChangeConfig({...config, name: e.target.value});
          }}
          disabled={disabled}
          {...getTestProps({component: 'VariableEditor', element: 'nameInput', index: `${index}`})}
        />

        <Select
          label="Type"
          required={true}
          value={combinedType}
          onChange={(newType) => {
            if (newType == null) {
              return;
            }
            const nextScalar = getDefaultConfigForType(newType);
            const nextDefinition = isMultiSelect ? convertDefinitionToArray(nextScalar) : nextScalar;
            onChangeConfig({
              ...config,
              definition: nextDefinition,
              value: isMultiSelect ? [] : null,
            });
          }}
          data={COMBINED_TYPE_OPTIONS}
          placeholder="Select a type"
          searchable={true}
          disabled={disabled}
          allowDeselect={false}
          // Default is 220px (~4 rows); bump it so all 13 options fit without scrolling.
          maxDropdownHeight={400}
          {...getTestProps({component: 'VariableEditor', element: 'typeSelect', index: `${index}`})}
        />

        {isDomain && isDomainDefinition(scalarDefinition) && (
          <DomainTypeSchemaSelector
            config={scalarDefinition}
            onChangeConfig={(newScalar) => {
              const nextDefinition = isMultiSelect ? convertDefinitionToArray(newScalar) : newScalar;
              onChangeConfig({
                ...config,
                definition: nextDefinition,
                value: isMultiSelect ? [] : null,
              });
            }}
            disabled={disabled}
          />
        )}

        {scalarDefinition?.kind === 'integer' && (
          <Group grow={true} align="flex-start">
            <NumberInput
              label="Minimum value"
              value={scalarDefinition.minValue ?? ''}
              onChange={(newValue) => {
                const nextScalar: NonArrayDefinition = {
                  ...scalarDefinition,
                  minValue: newValue === '' ? null : newValue,
                };
                const nextDefinition = isMultiSelect ? convertDefinitionToArray(nextScalar) : nextScalar;
                onChangeConfig({...config, definition: nextDefinition});
              }}
              precision={0}
              placeholder="No minimum"
              disabled={disabled}
            />
            <NumberInput
              label="Maximum value"
              value={scalarDefinition.maxValue ?? ''}
              onChange={(newValue) => {
                const nextScalar: NonArrayDefinition = {
                  ...scalarDefinition,
                  maxValue: newValue === '' ? null : newValue,
                };
                const nextDefinition = isMultiSelect ? convertDefinitionToArray(nextScalar) : nextScalar;
                onChangeConfig({...config, definition: nextDefinition});
              }}
              precision={0}
              placeholder="No maximum"
              disabled={disabled}
            />
          </Group>
        )}

        <Group spacing="lg" mt="xs">
          <Checkbox
            label="Required"
            checked={scalarDefinition?.required === true}
            onChange={(e) => {
              if (scalarDefinition == null) {
                return;
              }
              const nextScalar: NonArrayDefinition = {
                ...scalarDefinition,
                required: e.currentTarget.checked,
              };
              const nextDefinition = isMultiSelect ? convertDefinitionToArray(nextScalar) : nextScalar;
              onChangeConfig({...config, definition: nextDefinition});
            }}
            disabled={disabled}
          />
          <Checkbox
            label="Allow multiple values"
            checked={isMultiSelect}
            onChange={(e) => {
              if (e.currentTarget.checked) {
                onChangeConfig({
                  ...config,
                  definition: convertDefinitionToArray(config.definition),
                  value: config.value == null ? [] : [config.value],
                });
              } else {
                onChangeConfig({
                  ...config,
                  definition: convertDefinitionFromArray(config.definition),
                  value: Array.isArray(config.value) && config.value.length > 0 ? config.value[0] : null,
                });
              }
            }}
            disabled={disabled}
          />
        </Group>

        <VariableValueEditor
          definition={config.definition}
          value={config.value ?? null}
          isMultiSelect={isMultiSelect}
          onChange={(newValue) => {
            onChangeConfig({...config, value: newValue});
          }}
          disabled={disabled}
        />
      </Stack>
    </CollapsibleCard>
  );
}

/**
 * Custom editor for `DEFINE_TEMPLATE_VARIABLES` steps.
 *
 * Each variable has a definition (`config.variables[i].definition`) and a "reference" value which
 * conforms to that definition (`config.variables[i].value`). The reference value is used to test the
 * analysis as the user designs the template, and is replaced by user-provided input each time the
 * template is applied.
 *
 * The "Type" dropdown combines primitive kinds (Boolean, Decimal, Integer, Text) and Benchling
 * domain types (Plate, Container, etc.) into a single selector — domain selections are persisted as
 * `{kind: 'domain', types: [<type>]}`, while primitive selections are persisted as `{kind: <kind>}`.
 *
 * The reference value editor is delegated to `StepConfigEditorRouter` so each kind reuses the
 * standard per-kind input (string, integer, number, boolean, domain) and array values get the
 * standard add/remove array UI.
 */
export default function DefineTemplateVariablesEditor({
  config,
  onChangeConfig,
  disabled,
}: DefineTemplateVariablesEditorProps): JSX.Element {
  const variables = config.variables ?? [];

  return (
    <Stack spacing="sm">
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="Users will provide values for these variables each time the template is applied."
      />

      {variables.map((variable, index) => (
        <VariableEditorCard
          key={index}
          index={index}
          config={variable}
          canRemove={variables.length > 1}
          onChangeConfig={(newVariable) => {
            onChangeConfig(config, ['variables'], variables.toSpliced(index, 1, newVariable));
          }}
          onRemove={() => {
            onChangeConfig(config, ['variables'], immutableRemoveAt(variables, index));
          }}
          disabled={disabled}
        />
      ))}

      {!disabled && (
        <AddItemButton
          label="Add variable"
          onClick={() => {
            onChangeConfig(config, ['variables'], [...variables, {...DEFAULT_VARIABLE}]);
          }}
        />
      )}
    </Stack>
  );
}
